import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowLeft, Mail, Phone } from 'lucide-react';
import { siteConfig } from '../data/content';

const TermsPage = () => {
  const lastUpdated = "December 30, 2024";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Scale size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="terms-title">Terms of Use</h1>
              <p className="text-sm text-slate-500">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
              
              {/* Agreement */}
              <div>
                <h2 className="heading-3 mb-4">Agreement to Terms</h2>
                <p className="body-medium">
                  By accessing or using the {siteConfig.name} website, you agree to be bound by 
                  these Terms of Use. If you do not agree with any part of these terms, please 
                  do not use our website.
                </p>
              </div>

              {/* Website Use */}
              <div>
                <h2 className="heading-3 mb-4">Use of Website</h2>
                <p className="body-medium mb-4">Our website is intended to:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Provide general information about our speech therapy services</li>
                  <li>Allow you to submit general inquiries and appointment requests</li>
                  <li>Offer educational resources about dysphagia and related conditions</li>
                </ul>
              </div>

              {/* Medical Disclaimer */}
              <div>
                <h2 className="heading-3 mb-4">Medical Disclaimer</h2>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="body-medium text-amber-900">
                    <strong>Important:</strong> The information provided on this website is for 
                    general informational purposes only and is not intended to be a substitute 
                    for professional medical advice, diagnosis, or treatment. Always seek the 
                    advice of a qualified healthcare provider with any questions you may have 
                    regarding a medical condition.
                  </p>
                </div>
                <p className="body-medium mt-4">
                  Never disregard professional medical advice or delay seeking treatment because 
                  of information you have read on this website. If you think you may have a 
                  medical emergency, call your doctor or 911 immediately.
                </p>
              </div>

              {/* No PHI Submission */}
              <div>
                <h2 className="heading-3 mb-4">Health Information</h2>
                <p className="body-medium mb-4">
                  Our website contact forms are designed for general inquiries only. 
                  Please do not submit:
                </p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Detailed medical history or diagnoses</li>
                  <li>Insurance member IDs or card images</li>
                  <li>Medical records or test results</li>
                  <li>Prescription or medication information</li>
                  <li>Any other protected health information (PHI)</li>
                </ul>
                <p className="body-medium mt-4">
                  Sensitive health information should only be shared through approved healthcare 
                  channels and during scheduled appointments.
                </p>
              </div>

              {/* Intellectual Property */}
              <div>
                <h2 className="heading-3 mb-4">Intellectual Property</h2>
                <p className="body-medium">
                  All content on this website, including text, graphics, logos, and images, 
                  is the property of {siteConfig.name} and is protected by copyright and other 
                  intellectual property laws. You may not reproduce, distribute, or use any 
                  content without our prior written permission.
                </p>
              </div>

              {/* User Conduct */}
              <div>
                <h2 className="heading-3 mb-4">User Conduct</h2>
                <p className="body-medium mb-4">When using our website, you agree not to:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Submit false or misleading information</li>
                  <li>Interfere with the proper functioning of the website</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Use the website for any unlawful purpose</li>
                  <li>Harass, abuse, or harm others through the website</li>
                </ul>
              </div>

              {/* Limitation of Liability */}
              <div>
                <h2 className="heading-3 mb-4">Limitation of Liability</h2>
                <p className="body-medium">
                  To the fullest extent permitted by law, {siteConfig.name} shall not be liable 
                  for any indirect, incidental, special, consequential, or punitive damages 
                  arising out of or related to your use of the website or services.
                </p>
              </div>

              {/* Third-Party Links */}
              <div>
                <h2 className="heading-3 mb-4">Third-Party Links</h2>
                <p className="body-medium">
                  Our website may contain links to third-party websites. We are not responsible 
                  for the content, privacy practices, or accuracy of any third-party sites.
                </p>
              </div>

              {/* Changes to Terms */}
              <div>
                <h2 className="heading-3 mb-4">Changes to Terms</h2>
                <p className="body-medium">
                  We reserve the right to modify these terms at any time. Changes will be 
                  effective immediately upon posting. Your continued use of the website 
                  constitutes acceptance of the modified terms.
                </p>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="heading-3 mb-4">Governing Law</h2>
                <p className="body-medium">
                  These Terms of Use shall be governed by and construed in accordance with 
                  the laws of the State of Texas, without regard to its conflict of law provisions.
                </p>
              </div>

              {/* Contact */}
              <div className="pt-6 border-t border-slate-200">
                <h2 className="heading-3 mb-4">Contact Us</h2>
                <p className="body-medium mb-4">
                  If you have questions about these Terms of Use, please contact us:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-teal-600" />
                    <a href={`tel:${siteConfig.phone}`} className="link">{siteConfig.phone}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-teal-600" />
                    <a href={`mailto:${siteConfig.email}`} className="link">{siteConfig.email}</a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
